import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { notificationStore, sectionStore } from '@/lib/variables'
import Header from '@/components/layout/Header'
import HomeHero from '@/components/sections/HomeHero'
import AboutSection from '@/components/sections/AboutSection'
import ContactSection from '@/components/sections/ContactSection'
import FAQSection from '@/components/sections/FAQSection'
import ProjectsSection from '@/components/sections/ProjectSection/ProjectsSection'
import Footer from '@/components/layout/Footer'
import Notification from '@/components/ui/Notification'
import StorageNotice from '@/components/ui/StorageNotice'
import Announcement from '@/components/ui/Announcement'
import { localePath, splitLocale } from '@/lib/seo'
import { ThemeProvider } from '@/lib/theme'
import { SECTION_PATHS, sectionForPath, type Section } from '@/lib/sections'
import { PrimarySectionContext } from '@/lib/primarySection'

// Unprefixed: /en/services is the 'about' section too.
const pathToSection = (pathname: string): Section => sectionForPath(splitLocale(pathname).path)

type Toast = { message: string; type?: 'success' | 'error' | 'info' }

export default function ClientShell({ initial = 'home' }: { initial?: Section }) {
  const [active, setActive] = useState<Section>(initial)
  // Fixed at mount: the URL the page was loaded (and prerendered) for.
  const [primary] = useState<Section>(initial)
  const navigate = useNavigate()
  // While a click-triggered smooth scroll runs, the scroll-spy would flick
  // through every section in between; ignore it until the scroll settles.
  const programmaticRef = useRef(false)
  const [notification, setNotification] = useState<Toast | null>(null)

  useEffect(() => {
    const handler = (e: Event) => setNotification((e as CustomEvent<Toast>).detail)
    notificationStore.on('notify', handler)
    return () => notificationStore.off('notify', handler)
  }, [])

  const refs = useRef<Record<Section, HTMLElement | null>>({
    home: null,
    about: null,
    faq: null,
    projects: null,
    contact: null,
  })
  const setSectionRef = (key: Section) => (el: HTMLElement | null) => {
    refs.current[key] = el
  }

  const scrollToSection = (section: Section) => {
    if (section === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    refs.current[section]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Through the router (not raw history) so ShellPage sees the new path
  // and updates the title / canonical. The language prefix is kept (read
  // from the URL: the scroll-spy below holds the first render's goTo).
  const goTo = (section: Section, replace: boolean) => {
    const pathname = localePath(SECTION_PATHS[section], splitLocale(window.location.pathname).locale)
    if (window.location.pathname === pathname) return
    navigate({ pathname, search: window.location.search }, { replace })
  }

  const handleNavigate = (section: Section) => {
    goTo(section, false)
    setActive(section)
    programmaticRef.current = true
    const settle = () => {
      programmaticRef.current = false
      window.removeEventListener('scrollend', settle)
    }
    window.addEventListener('scrollend', settle)
    window.setTimeout(settle, 1200) // browsers without scrollend
    requestAnimationFrame(() => scrollToSection(section))
  }

  // Buttons deep in a section ("ask about this service") scroll the shell.
  useEffect(() => sectionStore.on((s) => handleNavigate(s as Section)), [])

  // Initial mount: jump to the section named by the URL (no smooth scroll).
  useEffect(() => {
    const current = pathToSection(window.location.pathname)
    setActive(current)
    if (current !== 'home') {
      // The spy's first report is the top of the page ("home"), before this
      // jump lands; ignoring it keeps a deep link like /contact from being
      // rewritten to /.
      programmaticRef.current = true
      const settle = () => {
        programmaticRef.current = false
        window.removeEventListener('scrollend', settle)
      }
      const id = setTimeout(() => {
        refs.current[current]?.scrollIntoView({ block: 'start' })
        window.addEventListener('scrollend', settle)
      }, 0)
      const fallback = setTimeout(settle, 1200)
      return () => {
        clearTimeout(id)
        clearTimeout(fallback)
        settle()
      }
    }
  }, [])

  // Back/forward navigation.
  useEffect(() => {
    const onPop = () => {
      const current = pathToSection(window.location.pathname)
      setActive(current)
      setTimeout(() => scrollToSection(current), 0)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  // Highlight the section in view while scrolling. Only after the page has
  // actually scrolled: crawlers render with a viewport as tall as the page
  // and never scroll, so the spy would otherwise "see" a middle section and
  // rewrite / to /projects — taking the title, description and canonical
  // with it (Google then snippeted the projects copy for the home page).
  const scrolledRef = useRef(false)
  useEffect(() => {
    const onScroll = () => {
      scrolledRef.current = true
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const entries = Object.entries(refs.current).filter(([, el]) => el) as [Section, HTMLElement][]
    if (!('IntersectionObserver' in window) || entries.length === 0) return
    const obs = new IntersectionObserver(
      (list) => {
        const visible = list.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
        if (!visible) return
        const hit = entries.find(([, el]) => el === visible.target)
        if (!hit || programmaticRef.current || !scrolledRef.current) return
        setActive(hit[0])
        // Follow the scroll in the address bar, without stacking history.
        goTo(hit[0], true)
      },
      { rootMargin: '-40% 0px -50% 0px', threshold: [0, 0.2, 0.5] },
    )
    entries.forEach(([, el]) => obs.observe(el))
    return () => obs.disconnect()
  }, [])

  return (
    <ThemeProvider>
        <Header active={active} onNavigate={handleNavigate} />

        <PrimarySectionContext.Provider value={primary}>
        <main className="container">
          <section id="home" ref={setSectionRef('home')} className="snap-section">
            <HomeHero onNavigate={handleNavigate} />
          </section>

          <section id="projects" ref={setSectionRef('projects')} className="snap-section">
            <ProjectsSection />
          </section>

          <section id="about" ref={setSectionRef('about')} className="snap-section">
            <AboutSection />
          </section>

          <section id="faq" ref={setSectionRef('faq')} className="snap-section">
            <FAQSection />
          </section>

          <section id="contact" ref={setSectionRef('contact')} className="snap-section">
            <ContactSection />
          </section>
        </main>
        </PrimarySectionContext.Provider>

        <Footer />
        <StorageNotice />
        <Announcement onNavigate={handleNavigate} />

        {notification?.message && <Notification message={notification.message} type={notification.type} />}
    </ThemeProvider>
  )
}
